// @ts-nocheck
import React from 'react';
import { Element, Feature, Record } from '@fulcrumapp/fulcrum-core';
import { Alert, Snackbar, CircularProgress } from '@mui/material';
import Portal from '@mui/material/Portal';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { styled } from '@mui/material/styles';
import PropTypes from 'react-proptypes';
import cx from 'classnames';
import DataViewSource from '../../data-view/models/data-view-source';
import Modal from '../../shared/modal';
import Poller from '../../shared/poller';
import { Export } from '../../models/export';
import CloseIcon from '../../icons/close';
import PhotoGalleryItem from './photo-gallery-item-view';
import { FormAppContext } from '../../hooks/use-form-app';
import Button from '../../components/button';

export const forbiddenMessage = 'Your current role does not have the Export Records permission.';
export const createdMessage = 'The file is being processed. Download will start automatically once it\'s ready.';
interface State {
  message: string,
  toastOpen: boolean,
  submitting: boolean,
  includePhotos: boolean,
  includeVideos: boolean,
  format: string,
  items: Export[],
  record: Record,
  isError: boolean,
}

const POLLER = new Poller();

const getDownloadableMediaElements = (elements: Element[]) => {
  const downloadableMediaElements = [];

  for (const element of elements) {
    if (element.isPhotoElement || element.isVideoElement || element.isRepeatableElement) {
      downloadableMediaElements.push(element);
    } else if (element.isSectionElement) {
      downloadableMediaElements.push(...getDownloadableMediaElements(element.elements));
    }
  }

  return downloadableMediaElements;
};

export default class PhotoGalleryView extends React.Component {
  static propTypes = {
    source: PropTypes.instanceOf(DataViewSource),
    visible: PropTypes.bool.isRequired,
    mediaValueItems: PropTypes.instanceOf(Array),
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    record: PropTypes.instanceOf(Record),
    feature: PropTypes.instanceOf(Feature),
  };

  static contextType = FormAppContext;

  state: State = {
    message: '',
    isError: false,
    toastOpen: false,
    submitting: false,
    includePhotos: true,
    includeVideos: true,
    format: 'none',
  };

  componentWillUnmount() {
    POLLER.stop();
  }

  handleDownloadClick() {
    this.setState({
      toastOpen: true,
      submitting: true,
      message: createdMessage,
      isError: false,
    }, () => {
      const json = this.props.source.query.toJSON();

      // Find and explicitly add photo gallery supported elements to columns to export
      // datadownloader needs these to know what media to include
      const elements = getDownloadableMediaElements(this.props.record.form.elements);
      const mediaColumns = elements
        .map(element => ({ column: { id: element.key }, hidden: false }));
      json.columns = mediaColumns;

      const query = {
        ...json,
        form_id: this.props.record.form.id,
        bounding_box: null,
        filter: {
          expressions: [
            {
              field: '_record_id',
              operator: 'text_equal',
              value: [this.props.record.id],
            },
          ],
        },
      };

      const attributes = {
        queries: [query],
        options: {
          include_photos: this.state.includePhotos,
          include_videos: this.state.includeVideos,
        },
        format: this.state.format,

      };
      this.props.source.dataSource?.source.createExport(attributes, (error, exportJSON) => {
        if (error) {
          let { message } = error;
          if (error.res.status === 403) {
            message = forbiddenMessage;
          }
          this.setState({
            toastOpen: true, submitting: false, message, isError: true,
          });
          return;
        }
        if (exportJSON) {
          const { id, state } = exportJSON;
          this.setState({ exportId: id });
          if (state === 'running') {
            POLLER.start(this.handleFetch.bind(this, id), this.handleSuccess, this.handleError);
          }
        }
      });
    });
  }

  handleFetch = async (exportId: string) => {
    const exportResp = await this.context.api.exports.find(exportId);
    return exportResp;
  }

  handleSuccess = (exportItem: Export) => {
    if (exportItem?.state === 'completed' && exportItem?.error === null) {
      window.open(exportItem.file, '_self');
      this.setState({
        toastOpen: false, message: '', submitting: false, isError: false,
      });
      POLLER.stop();
    } else if (exportItem?.state === 'failed' || exportItem?.error !== null) {
      this.setState({ isError: true, message: 'There was an error processing your download. Please try again.', submitting: true });
      POLLER.stop();
    }
  };

  handleError = (err: Error) => {
    console.error(err);
    this.setState({ toastOpen: false, message: '', submitting: false });
    POLLER.stop();
  }

  handleCancelClick = () => {
    POLLER.stop();
    this.props.source.dataSource?.source.cancelExport(this.state.exportId);
    this.setState({
      toastOpen: false, submitting: false, message: '', isError: false,
    });
  }

  render() {
    const { styles } = PhotoGalleryView;

    return (
      <>
        <Modal
          show={this.props.visible}
          onHide={null}
          customModalStyles={{ zIndex: '660' }}
        >
          <div className={cx('modal-content', styles.modal)}>
            <div className={cx(styles.headerContainer)}>
              {this.renderTitle()}
              <div className={cx(styles.downloadButtonContainer)}>
                <Button
                  sx={{
                    marginRight: 2,
                    marginBottom: 0,
                    whiteSpace: 'nowrap',
                  }}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  onClick={() => this.handleDownloadClick()}
                  disabled={this.state.submitting}
                >
                  Download All
                </Button>
              </div>
              <div
                className={this.state.submitting ? cx(styles.close, styles.closeDisabled) : cx(styles.close)}
                onClick={this.state.submitting ? null : this.props.onClose}
              >
                <CloseIcon style={this.state.submitting ? { fill: '#d9d9d9' } : { fill: '#f00' }} height="28px" width="28px" />
              </div>
            </div>
            <div className={cx(styles.items)}>
              {this.renderItems()}
            </div>
          </div>
        </Modal>
        <Portal>
          <Snackbar
            open={this.state.toastOpen}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              sx={{
                width: '100%',
                backgroundColor: '#323232',
                color: '#fff',
                alignItems: 'center',
                '& .MuiAlert-action': { padding: '0 !important' },
              }}
              className={cx(styles.alert)}
              icon={this.state.isError ? <ErrorOutline size={24} color="error" /> : <CircularProgress size={24} style={{ color: '#4BAEF8' }} />}
              onClose={this.state.isError ? () => {
                this.setState({
                  toastOpen: false, message: '', submitting: false, isError: false,
                });
              } : null}
            >
              {this.state.message}
              <Button
                color="error"
                onClick={this.state.isError ? () => this.handleDownloadClick() : this.handleCancelClick}
              >
                {this.state.isError ? 'Retry' : 'Cancel'}
              </Button>
            </Alert>
          </Snackbar>
        </Portal>
      </>
    );
  }

  renderTitle() {
    const { styles } = PhotoGalleryView;

    return (
      <div className={styles.caption}>
        <span className={styles.captionLabel}>{this.props.title}</span>
      </div>
    );
  }

  renderItems() {
    return this.props.mediaValueItems?.map((item) => (
      <PhotoGalleryItem
        key={item.mediaID}
        item={item}
        mediaValueItems={this.props.mediaValueItems}
        record={this.props.record}
        feature={this.props.feature}
        metadata={null}
      />
    ));
  }
}

const styles = cssInJS({
  modal: {
    backgroundColor: '#fff',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    minHeight: 300,
    height: '100%',

    '@phone': {
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
  },

  headerContainer: {
    fontFamily: THEME.fontFamily,
    borderBottom: '1px solid #d9d9d9',
    borderTop: '5px solid red',
    height: 55,
    marginBottom: 10,
    display: 'flex',
  },

  close: {
    width: 50,
    height: 50,
    float: 'right',
    padding: '11px 8px',
    cursor: 'pointer',
    borderLeft: '1px solid #d9d9d9',
    fill: '#eb1300',
  },

  closeDisabled: {
    fill: '#d9d9d9',
  },

  caption: {
    marginTop: 14,
    marginLeft: 16,
    paddingRight: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',

    '@phone': {
      marginRight: 14,
    },
  },

  captionLabel: {
    marginRight: 5,
  },

  items: {
    paddingLeft: 16,
    overflow: 'scroll',
    height: '100%',
  },
  downloadButtonContainer: {
    padding: 2,
    display: 'flex',
    alignItems: 'center',
  },
});

PhotoGalleryView.styles = styles;
